import { addDays, differenceInCalendarDays, format, max, min, parse } from 'date-fns';

import { TDaysAvailability, TSelectedRange } from 'shared/types/bookingCalendar';

export const DEFAULT_INPUT_TEXT = 'Заезд – Выезд';

export const formatDate = (date: Date) => format(date, 'yyyy-MM-dd');

export const isDayAvailable = (availabilityArray: TDaysAvailability, date: Date): boolean => {
  const today = new Date();
  const index = differenceInCalendarDays(date, today);

  // если календарь проверяет даты за пределами проверенных на доступность, возвращать их доступными
  if (availabilityArray[index] === undefined || index < 0) {
    return true;
  }

  // 0 – занято, 1 – свободно
  return !!availabilityArray[index];
};

/** Ограничивает диапазон ближайшей занятой датой, если она попадается внутри этого диапазона */
export const getNormalizedEndDate = (
  minDate: Date,
  startDate: Date,
  endDate: Date,
  daysAvailability: TDaysAvailability,
) => {
  /** Количество дней от сегодняшнего до начальной даты диапазона */
  const index = Math.abs(differenceInCalendarDays(minDate, startDate));

  /** Количество дней в выбранном диапазоне */
  const daysInRange = Math.abs(differenceInCalendarDays(startDate, endDate));

  /** Массив доступности, начиная со следующего дня после выбранной начальной даты */
  const slicedAvailability = daysAvailability.slice(index + 1);

  /** Количество дней от начальной даты диапазона до следующей недоступной даты */
  const daysUntilUnavailable = slicedAvailability.findIndex(d => !d);

  let normalizedEndDate = endDate;
  if (daysUntilUnavailable !== -1 && daysInRange > daysUntilUnavailable) {
    normalizedEndDate = addDays(startDate, daysUntilUnavailable);
  }

  return normalizedEndDate;
};

enum ValidationRuleTitle {
  NO_RANGE = 'noRange',
  MIN_RANGE = 'minRange',
  MAX_RANGE = 'maxRange',
}

const ValidationRulesErrorMap: Record<ValidationRuleTitle, string> = {
  [ValidationRuleTitle.NO_RANGE]: 'Даты не выбраны',
  [ValidationRuleTitle.MIN_RANGE]: 'Выберите разные дни заезда и выезда',
  [ValidationRuleTitle.MAX_RANGE]: 'Максимум 30 ночей',
};

const validationRules: Record<ValidationRuleTitle, (selection: TSelectedRange) => boolean> = {
  [ValidationRuleTitle.NO_RANGE]: (selection: TSelectedRange) => {
    const { fromValue, toValue } = selection;

    return !!fromValue || !!toValue;
  },
  [ValidationRuleTitle.MIN_RANGE]: (selection: TSelectedRange) => {
    const { fromValue, toValue } = selection;
    if (!fromValue || !toValue) {
      return false;
    }

    return Math.abs(differenceInCalendarDays(fromValue, toValue)) > 0;
  },
  [ValidationRuleTitle.MAX_RANGE]: (selection: TSelectedRange) => {
    const { fromValue, toValue } = selection;
    if (!fromValue || !toValue) {
      return false;
    }

    return Math.abs(differenceInCalendarDays(fromValue, toValue)) <= 30;
  },
};

export const getValidationErrorMessage = (selection: TSelectedRange) => {
  const invalidRuleIndex = Object.values(validationRules).findIndex(predicate => !predicate(selection));
  if (invalidRuleIndex === -1) {
    return '';
  }

  const invalidRuleKey = Object.keys(validationRules)[invalidRuleIndex] as ValidationRuleTitle;

  return ValidationRulesErrorMap[invalidRuleKey];
};

export const updateUrlWithCheckInAndCheckOut = (
  currentUrl: string,
  dates: { checkin: string; checkout: string },
): string => {
  const url = new URL(currentUrl);

  url.searchParams.delete('checkin');
  url.searchParams.delete('checkout');

  url.searchParams.set('checkin', dates.checkin);
  url.searchParams.set('checkout', dates.checkout);

  return url.toString();
};

export const deleteDailyrentQueryParams = (currentUrl: string) => {
  const url = new URL(currentUrl);

  url.searchParams.delete('checkin');
  url.searchParams.delete('checkout');

  return url.toString();
};

export const getNormalizedInitialDates = (
  minDate: Date,
  dates: { from?: string; to?: string },
  daysAvailability: TDaysAvailability,
) => {
  let startDate = dates.from ? parse(dates.from, 'yyyy-MM-dd', new Date()) : null;
  let endDate = dates.to ? parse(dates.to, 'yyyy-MM-dd', new Date()) : null;

  if (!startDate || !endDate) {
    return { normalizedInitialStart: null, normalizedInitialEnd: null };
  }

  startDate = min([startDate, endDate]);
  const latestDate = max([startDate, endDate]);
  endDate = getNormalizedEndDate(minDate, startDate, latestDate, daysAvailability);

  return { normalizedInitialStart: startDate, normalizedInitialEnd: endDate };
};
